import React, { useMemo, useState } from "react";
import { EnumStatusConsultation } from "../../interfaces/history";
import { IStatusChangeLog } from "../../services/historyCpfService";

type Props = {
    show: boolean;
    onHide: () => void;
    protocol?: string;
    currentStatus?: number;
    logs: IStatusChangeLog[];
    logsFrom: string;
    logsTo: string;
    setLogsFrom: (v: string) => void;
    setLogsTo: (v: string) => void;
    reloadLogs: () => void;

    onConfirm: (newStatus: number, notes?: string) => void;
    loading?: boolean;
};

const statusOptions = [
    { value: EnumStatusConsultation.approved, label: "Aprovado" },
    { value: EnumStatusConsultation.disapproved, label: "Reprovado" },
    { value: EnumStatusConsultation.inProcess, label: "Em processo" },
    { value: EnumStatusConsultation.completed, label: "Finalizado" },
    { value: EnumStatusConsultation.inAnalyzing, label: "Em análise" },
];

function statusLabel(v?: number) {
    return statusOptions.find(o => o.value === v)?.label ?? "—";
}

const ChangeCpfStatusModal: React.FC<Props> = ({
    show, onHide, protocol, currentStatus,
    logs, logsFrom, logsTo, setLogsFrom, setLogsTo, reloadLogs,
    onConfirm, loading
}) => {
    const [newStatus, setNewStatus] = useState<number | undefined>(currentStatus);
    const [notes, setNotes] = useState("");

    React.useEffect(() => {
        setNewStatus(currentStatus);
        setNotes("");
    }, [currentStatus, protocol, show]);

    if (!show) return null;

    return (
        <div className="modal d-block" tabIndex={-1}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Alterar status – Protocolo {protocol}</h5>
                        <button type="button" className="btn-close" onClick={onHide} />
                    </div>

                    <div className="modal-body">
                        <div className="mb-3">
                            <label className="form-label">Status atual</label>
                            <div><strong>{statusLabel(currentStatus)}</strong></div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Novo status</label>
                            <select
                                className="form-select"
                                value={newStatus}
                                onChange={(e) => setNewStatus(parseInt(e.target.value))}
                            >
                                {statusOptions.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Observação (opcional)</label>
                            <textarea
                                className="form-control"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={2}
                            />
                        </div>

                        <hr />

                        <div className="d-flex align-items-end justify-content-between">
                            <div className="row g-2">
                                <div className="col">
                                    <label className="form-label">De</label>
                                    <input type="date" className="form-control" value={logsFrom} onChange={e => setLogsFrom(e.target.value)} />
                                </div>
                                <div className="col">
                                    <label className="form-label">Até</label>
                                    <input type="date" className="form-control" value={logsTo} onChange={e => setLogsTo(e.target.value)} />
                                </div>
                            </div>
                            <button className="btn btn-sm btn-outline-secondary ms-2" onClick={reloadLogs}>Filtrar</button>
                        </div>

                        <div className="mt-3" style={{ maxHeight: 260, overflowY: "auto" }}>
                            {logs.length === 0 ? (
                                <div className="text-muted">Sem alterações registradas no período.</div>
                            ) : (
                                <table className="table table-sm align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Data</th>
                                            <th>De → Para</th>
                                            <th>Usuário</th>
                                            {/* <th>E-mail</th> */}
                                            <th>IP</th>
                                            <th>Obs.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map(l => (
                                            <tr key={l.id}>
                                                <td>{new Date(l.changedAt).toLocaleString()}</td>
                                                <td><strong>{statusLabel(l.oldStatus)}</strong> → <strong>{statusLabel(l.newStatus)}</strong></td>
                                                <td>{l.changedByName || "—"}</td>
                                                {/* <td>{l.changedByEmail || "—"}</td> */}
                                                <td>{l.clientIp || "—"}</td>
                                                <td style={{ maxWidth: 240, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                                                    {l.notes || "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onHide} disabled={loading}>Cancelar</button>
                        <button
                            className="btn btn-primary"
                            onClick={() => newStatus !== undefined && onConfirm(newStatus, notes)}
                            disabled={loading}
                        >
                            {loading ? "Salvando..." : "Confirmar"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangeCpfStatusModal;